import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { UserPlus } from "lucide-react";
import { PageHeader, Field, SectionTitle } from "@/components/Bits";
import CoverImageField from "@/components/CoverImageField";
import NewClientDialog from "@/components/NewClientDialog";
import ProjectNav from "@/components/ProjectNav";

const TYPES = ["Residential Interior", "Commercial Interior", "Experiential", "Set Design", "Seasonal Installation", "Event Installation", "Other"];
const STATUSES = ["Lead", "Active", "On Hold", "Completed", "Archived"];
const PHASES = ["Discovery", "Concept Development", "Design Development", "Client Review", "Sourcing", "Procurement", "Production", "Installation", "Styling", "Complete"];

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";

export default function ProjectForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientDialog, setClientDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", client_id: "", project_type: TYPES[0], status: "Active", phase: "Discovery",
    description: "", address: "", start_date: "", target_completion_date: "", install_date: "",
    cover_image: "", cover_file_id: "", cover_focus: "", budget_total: 0, design_fee: 0, procurement_budget: 0, installation_budget: 0,
    contingency: 0, tax_allowance: 0, shipping_allowance: 0, progress: 0, client_portal_enabled: false,
  });

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    if (editing) {
      api.get(`/projects/${id}`).then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
    }
  }, [id, editing]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const cancel = async () => {
    // Drop an orphan upload if the new project is abandoned.
    if (!editing && form.cover_file_id) {
      await api.delete(`/files/${form.cover_file_id}`).catch(() => {});
    }
    navigate(-1);
  };

  const submit = async (e) => {    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      budget_total: Number(form.budget_total) || 0,
      design_fee: Number(form.design_fee) || 0,
      procurement_budget: Number(form.procurement_budget) || 0,
      installation_budget: Number(form.installation_budget) || 0,
      contingency: Number(form.contingency) || 0,
      tax_allowance: Number(form.tax_allowance) || 0,
      shipping_allowance: Number(form.shipping_allowance) || 0,
      progress: Number(form.progress) || 0,
    };
    try {
      const { data } = editing
        ? await api.put(`/projects/${id}`, payload)
        : await api.post("/projects", payload);
      // A cover uploaded before the project existed is only now attachable to it.
      if (!editing && payload.cover_file_id) {
        await api
          .patch(`/files/${payload.cover_file_id}`, { project_id: data.id, visible_to_client: true })
          .catch(() => {});
      }
      toast.success(editing ? "Project updated" : "Project created");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-10 pb-10" data-testid="project-form">
      <ProjectNav
        projectId={editing ? id : null}
        projectName={form.name || "Project"}
        trail={[{ label: editing ? "Edit Project" : "New Project" }]}
      />
      <NewClientDialog
        open={clientDialog}
        existing={clients}
        onClose={() => setClientDialog(false)}
        onCreated={(client) => {
          // Keep every project field the user already typed; just select the new client.
          setClients((list) => (list.some((c) => c.id === client.id) ? list : [...list, client]));
          setForm((f) => ({ ...f, client_id: client.id }));
        }}
      />
      <PageHeader
        overline={editing ? "Edit" : "New"}
        title={editing ? "Edit Project" : "Create Project"}
        subtitle="Basic information, schedule and financial framework."
        actions={
          <>
            <button type="button" onClick={cancel} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:bg-line/50">
              Cancel
            </button>
            <button data-testid="project-form-submit" type="submit" disabled={busy} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60">
              {busy ? "Saving…" : editing ? "Save Project" : "Create Project"}
            </button>
          </>
        }
      />

      <section className="card-surface p-7 sm:p-9">
        <SectionTitle>Basic Information</SectionTitle>
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Project Name">
            <input data-testid="project-name-input" required value={form.name} onChange={set("name")} className={input} />
          </Field>
          <Field label="Client">
            <div className="flex gap-2">
              <select data-testid="project-client-select" value={form.client_id || ""} onChange={set("client_id")} className={input}>
                <option value="">— No client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.company ? `${c.name} · ${c.company}` : c.name}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid="add-new-client-btn"
                onClick={() => setClientDialog(true)}
                title="Add a new client"
                className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-line bg-white px-3 py-2.5 text-sm text-ink transition-colors hover:border-lime"
              >
                <UserPlus className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Add New</span>
              </button>
            </div>
          </Field>
          <Field label="Project Type">
            <select data-testid="project-type-select" value={form.project_type} onChange={set("project_type")} className={input}>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="project-status-select" value={form.status} onChange={set("status")} className={input}>
              {STATUSES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Project Phase">
            <select data-testid="project-phase-select" value={form.phase} onChange={set("phase")} className={input}>
              {PHASES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Progress %">
            <input data-testid="project-progress-input" type="number" min="0" max="100" value={form.progress} onChange={set("progress")} className={`num ${input}`} />
          </Field>
          <Field label="Project Description" className="md:col-span-2">
            <textarea data-testid="project-description-input" rows={4} value={form.description || ""} onChange={set("description")} className={input} />
          </Field>
          <Field label="Project Address" className="md:col-span-2">
            <input data-testid="project-address-input" value={form.address || ""} onChange={set("address")} className={input} />
          </Field>
          <Field label="Start Date">
            <input data-testid="project-start-date-input" type="date" value={(form.start_date || "").slice(0, 10)} onChange={set("start_date")} className={input} />
          </Field>
          <Field label="Target Completion Date">
            <input data-testid="project-target-date-input" type="date" value={(form.target_completion_date || "").slice(0, 10)} onChange={set("target_completion_date")} className={input} />
          </Field>
          <Field label="Install Date">
            <input data-testid="project-install-date-input" type="date" value={(form.install_date || "").slice(0, 10)} onChange={set("install_date")} className={input} />
          </Field>
          <div className="md:col-span-2">
            <CoverImageField
              projectId={editing ? id : null}
              value={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          </div>
        </div>
      </section>

      <section className="card-surface p-7 sm:p-9">
        <SectionTitle>Financial Information</SectionTitle>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["budget_total", "Overall Budget"],
            ["design_fee", "Design Fee"],
            ["procurement_budget", "Procurement Budget"],
            ["installation_budget", "Installation Budget"],
            ["contingency", "Contingency"],
            ["tax_allowance", "Tax Allowance"],
            ["shipping_allowance", "Shipping Allowance"],
          ].map(([k, label]) => (
            <Field key={k} label={label}>
              <input data-testid={`project-${k.replace(/_/g, "-")}-input`} type="number" step="0.01" value={form[k]} onChange={set(k)} className={`num ${input}`} />
            </Field>
          ))}
        </div>
        <label className="mt-8 flex items-center gap-3 text-sm text-ink">
          <input data-testid="project-portal-toggle" type="checkbox" checked={!!form.client_portal_enabled} onChange={set("client_portal_enabled")} className="h-4 w-4 accent-[#BEC221]" />
          Enable client portal access for this project
        </label>
      </section>
    </form>
  );
}
